/**
 * Inline Account Value (NLV) input component.
 * Displays current value with an edit button, or an input form when editing.
 * Shows "last updated" timestamp so the user knows how stale the value is.
 */

import { useState, useCallback, useEffect } from 'react';
import { Button, Spinner } from '@/components/ui';
import { formatCurrency, formatDateTime } from '@/utils/formatters';

interface NlvInputProps {
  nlv: number | null;
  nlvUpdatedAt: string | null;
  isSaving: boolean;
  saveError: string | null;
  onSave: (nlv: number) => Promise<void>;
  totalCostBasis?: number;
  nlvSource?: 'tastytrade' | 'manual' | null;
}

export function NlvInput({ nlv, nlvUpdatedAt, isSaving, saveError, onSave, totalCostBasis, nlvSource = null }: NlvInputProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    if (nlv !== null) {
      setInputValue(nlv.toString());
    }
  }, [nlv]);

  const handleEdit = useCallback(() => {
    setInputValue(nlv !== null ? nlv.toString() : '');
    setIsEditing(true);
  }, [nlv]);

  const handleCancel = useCallback(() => {
    setIsEditing(false);
    setInputValue(nlv !== null ? nlv.toString() : '');
  }, [nlv]);

  const handleSave = useCallback(async () => {
    const cleaned = inputValue.replace(/[,$\s]/g, '');
    const parsed = parseFloat(cleaned);

    // Reject non-numeric, negative, zero, NaN, Infinity
    if (!cleaned || isNaN(parsed) || !isFinite(parsed) || parsed <= 0) return;

    // Cap at a reasonable maximum
    if (parsed > 100_000_000_000) return;

    // Round to cents
    const rounded = Math.round(parsed * 100) / 100;

    try {
      await onSave(rounded);
      setIsEditing(false);
    } catch {
      // Error is handled by parent via saveError prop
    }
  }, [inputValue, onSave]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleSave();
      } else if (e.key === 'Escape') {
        handleCancel();
      }
    },
    [handleSave, handleCancel]
  );

  const showWarning = nlv !== null && totalCostBasis !== undefined && totalCostBasis > 0 && nlv < totalCostBasis;

  if (isEditing) {
    return (
      <div>
        <p className="text-sm text-dark-400">Account Value</p>
        <div className="flex items-center gap-2 mt-1">
          <div className="relative">
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-dark-400 text-sm">$</span>
            <input
              id="nlv-input"
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              className="w-32 rounded-md border border-dark-600 bg-dark-800 pl-5 pr-2 py-1 text-sm text-dark-100 font-mono focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              placeholder="150000"
              autoFocus
              disabled={isSaving}
            />
          </div>
          <Button variant="outline" size="sm" onClick={handleSave} disabled={isSaving}>
            {isSaving ? <Spinner size="sm" /> : 'Save'}
          </Button>
          <Button variant="outline" size="sm" onClick={handleCancel} disabled={isSaving}>
            Cancel
          </Button>
        </div>
        {saveError && <span className="text-xs text-rose-400 mt-1">{saveError}</span>}
      </div>
    );
  }

  return (
    <div>
      <p className="text-sm text-dark-400">Account Value</p>
      {nlv !== null ? (
        <div>
          <div className="flex items-center gap-1.5">
            <p className="text-xl font-bold font-mono tabular-nums text-dark-100">
              {formatCurrency(nlv)}
            </p>
            {nlvSource === 'tastytrade' ? (
              <span className="rounded bg-emerald-500/10 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-emerald-400">
                Live · Tastytrade
              </span>
            ) : nlvSource === 'manual' ? (
              <span className="rounded bg-dark-700 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-dark-400">
                Manual
              </span>
            ) : null}
            {showWarning && (
              <span
                className="inline-flex cursor-help"
                role="img"
                aria-label="Account Value is less than Total Cost Basis"
                title="Account Value is less than Total Cost Basis"
              >
                <svg className="h-4 w-4 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                </svg>
              </span>
            )}
          </div>
          <button
            onClick={handleEdit}
            className="text-xs text-primary-400 hover:text-primary-300 transition-colors mt-0.5"
            aria-label="Edit account value"
          >
            Edit
          </button>
          {nlvUpdatedAt && (
            <span className="text-xs text-dark-500 ml-2">
              ({formatDateTime(nlvUpdatedAt)})
            </span>
          )}
        </div>
      ) : (
        <div>
          <p className="text-xl font-bold font-mono tabular-nums text-dark-500">—</p>
          <button
            onClick={handleEdit}
            className="text-xs text-primary-400 hover:text-primary-300 transition-colors mt-0.5"
            aria-label="Set account value"
          >
            Set Value
          </button>
        </div>
      )}
    </div>
  );
}
